import {
  RiasecScores,
  CategoryWeights,
  SpecializationConfig,
  Recommendation,
  SchoolType,
} from '../typings/recommendation';
import { RiasecType } from '../typings/riasec';

/**
 * Specialization configurations with weights for all SMA and SMK options
 */
export const SPECIALIZATION_CONFIGS: SpecializationConfig[] = [
  // SMA Specializations
  {
    id: 'sma-ipa',
    name: 'IPA (Ilmu Pengetahuan Alam)',
    schoolType: 'SMA',
    weights: { r: 0.3, i: 0.5, a: 0.0, s: 0.0, e: 0.0, c: 0.2 },
    description:
      'Cocok untuk kamu yang suka mengamati, menganalisis, dan memecahkan masalah ilmiah. Kamu akan belajar Matematika, Fisika, Kimia, dan Biologi.',
    icon: '🔬',
    color: '#4A90E2',
  },
  {
    id: 'sma-ips',
    name: 'IPS (Ilmu Pengetahuan Sosial)',
    schoolType: 'SMA',
    weights: { r: 0.0, i: 0.0, a: 0.0, s: 0.4, e: 0.4, c: 0.2 },
    description:
      'Cocok untuk kamu yang suka berinteraksi dengan orang lain, memahami masyarakat, dan tertarik dengan ekonomi atau bisnis. Kamu akan belajar Ekonomi, Sosiologi, Geografi, dan Sejarah.',
    icon: '📊',
    color: '#4A90E2',
  },
  {
    id: 'sma-bahasa',
    name: 'Bahasa',
    schoolType: 'SMA',
    weights: { r: 0.0, i: 0.2, a: 0.4, s: 0.3, e: 0.0, c: 0.1 },
    description:
      'Cocok untuk kamu yang suka membaca, menulis, dan mempelajari bahasa serta budaya. Kamu akan belajar Bahasa Indonesia, Bahasa Inggris, dan bahasa asing lainnya.',
    icon: '📚',
    color: '#4A90E2',
  },
  // SMK Specializations
  {
    id: 'smk-teknik',
    name: 'Teknik (Mesin, Elektro, Informatika)',
    schoolType: 'SMK',
    weights: { r: 0.5, i: 0.3, a: 0.0, s: 0.0, e: 0.0, c: 0.2 },
    description:
      'Cocok untuk kamu yang suka bekerja dengan alat, mesin, atau komputer. Kamu akan belajar keterampilan praktis dalam bidang teknik dan teknologi.',
    icon: '⚙️',
    color: '#7ED321',
  },
  {
    id: 'smk-bisnis',
    name: 'Bisnis dan Manajemen',
    schoolType: 'SMK',
    weights: { r: 0.0, i: 0.0, a: 0.0, s: 0.2, e: 0.5, c: 0.3 },
    description:
      'Cocok untuk kamu yang suka memimpin, berbisnis, dan mengatur organisasi. Kamu akan belajar akuntansi, pemasaran, dan manajemen bisnis.',
    icon: '💼',
    color: '#7ED321',
  },
  {
    id: 'smk-seni',
    name: 'Seni dan Desain (Multimedia, DKV, Seni Rupa)',
    schoolType: 'SMK',
    weights: { r: 0.2, i: 0.2, a: 0.6, s: 0.0, e: 0.0, c: 0.0 },
    description:
      'Cocok untuk kamu yang suka berkreasi, menggambar, dan mengekspresikan ide secara visual. Kamu akan belajar desain grafis, multimedia, dan seni rupa.',
    icon: '🎨',
    color: '#7ED321',
  },
  {
    id: 'smk-kesehatan',
    name: 'Kesehatan (Keperawatan, Farmasi)',
    schoolType: 'SMK',
    weights: { r: 0.1, i: 0.3, a: 0.0, s: 0.4, e: 0.0, c: 0.2 },
    description:
      'Cocok untuk kamu yang suka membantu orang lain dan tertarik dengan kesehatan. Kamu akan belajar keperawatan, farmasi, atau asisten kesehatan.',
    icon: '⚕️',
    color: '#7ED321',
  },
  {
    id: 'smk-perhotelan',
    name: 'Perhotelan dan Pariwisata',
    schoolType: 'SMK',
    weights: { r: 0.0, i: 0.0, a: 0.2, s: 0.4, e: 0.3, c: 0.1 },
    description:
      'Cocok untuk kamu yang suka bertemu orang baru, melayani tamu, dan tertarik dengan industri pariwisata. Kamu akan belajar hospitality dan manajemen hotel.',
    icon: '🏨',
    color: '#7ED321',
  },
  {
    id: 'smk-tataboga',
    name: 'Tata Boga (Kuliner)',
    schoolType: 'SMK',
    weights: { r: 0.3, i: 0.0, a: 0.4, s: 0.2, e: 0.0, c: 0.1 },
    description:
      'Cocok untuk kamu yang suka memasak, berkreasi dengan makanan, dan tertarik dengan dunia kuliner. Kamu akan belajar teknik memasak dan manajemen dapur.',
    icon: '👨‍🍳',
    color: '#7ED321',
  },
];

/**
 * Validates RIASEC scores to ensure they are within valid range (0-11)
 * @throws Error if any score is invalid
 */
function validateRiasecScores(scores: RiasecScores): void {
  const categories: (keyof RiasecScores)[] = ['r', 'i', 'a', 's', 'e', 'c'];
  
  for (const category of categories) {
    const score = scores[category];
    
    if (score === undefined || score === null) {
      throw new Error(`RIASEC score for category '${category}' is missing`);
    }
    
    if (typeof score !== 'number' || isNaN(score)) {
      throw new Error(`RIASEC score for category '${category}' must be a number`);
    }
    
    if (score < 0 || score > 11) {
      throw new Error(
        `RIASEC score for category '${category}' must be between 0 and 11, got ${score}`
      );
    }
  }
}

/**
 * Validates category weights to ensure they are within valid range (0-1)
 * @throws Error if any weight is invalid
 */
function validateCategoryWeights(weights: CategoryWeights): void {
  const categories: (keyof CategoryWeights)[] = ['r', 'i', 'a', 's', 'e', 'c'];
  
  for (const category of categories) {
    const weight = weights[category];
    
    if (weight === undefined || weight === null) {
      throw new Error(`Weight for category '${category}' is missing`);
    }
    
    if (typeof weight !== 'number' || isNaN(weight)) {
      throw new Error(`Weight for category '${category}' must be a number`);
    }
    
    if (weight < 0 || weight > 1) {
      throw new Error(
        `Weight for category '${category}' must be between 0 and 1, got ${weight}`
      );
    }
  }
}

/**
 * Calculates match percentage for a specialization based on RIASEC scores and weights
 * 
 * Formula: (Σ(score × weight) / Σ(11 × weight)) × 100
 * 
 * @param scores - RIASEC scores (0-11 for each category)
 * @param weights - Category weights (0-1 for each category)
 * @returns Match percentage (0-100)
 */
export function calculateMatchPercentage(
  scores: RiasecScores,
  weights: CategoryWeights
): number {
  validateRiasecScores(scores);
  validateCategoryWeights(weights);
  
  const categories: (keyof RiasecScores)[] = ['r', 'i', 'a', 's', 'e', 'c'];
  
  let weightedSum = 0;
  let maxWeightedSum = 0;
  
  for (const category of categories) {
    const score = scores[category];
    const weight = weights[category];
    
    weightedSum += score * weight;
    maxWeightedSum += 11 * weight;
  }
  
  // Handle edge case where all weights are 0
  if (maxWeightedSum === 0) {
    return 0;
  }
  
  const percentage = (weightedSum / maxWeightedSum) * 100;
  
  // Round to 1 decimal place
  return Math.round(percentage * 10) / 10;
}

/**
 * Calculates all recommendations and sorts them by match percentage
 * 
 * @param scores - RIASEC scores from test results
 * @returns Array of recommendations sorted by match percentage (descending)
 */
export function calculateRecommendations(scores: RiasecScores): Recommendation[] {
  validateRiasecScores(scores);
  
  const recommendations: Recommendation[] = SPECIALIZATION_CONFIGS.map((config) => {
    const matchPercentage = calculateMatchPercentage(scores, config.weights);
    
    // Determine primary categories (those with highest weights)
    const weightEntries = Object.entries(config.weights) as [keyof CategoryWeights, number][];
    const sortedWeights = weightEntries.sort((a, b) => b[1] - a[1]);
    
    // Get categories with non-zero weights, prioritizing highest weights
    const primaryCategories: RiasecType[] = sortedWeights
      .filter(([_, weight]) => weight > 0)
      .slice(0, 3) // Take top 3
      .map(([category, _]) => category.toUpperCase() as RiasecType);
    
    return {
      id: config.id,
      name: config.name,
      schoolType: config.schoolType,
      matchPercentage,
      description: config.description,
      primaryCategories,
      icon: config.icon,
      color: config.color,
    };
  });
  
  // Sort by match percentage (descending), maintain stable sort for equal percentages
  recommendations.sort((a, b) => {
    if (b.matchPercentage !== a.matchPercentage) {
      return b.matchPercentage - a.matchPercentage;
    }
    // Stable sort: maintain original order for equal percentages
    return 0;
  });
  
  return recommendations;
}

/**
 * Gets a user-friendly explanation of how the match percentage was calculated
 * 
 * @param scores - RIASEC scores from test results
 * @param recommendation - The recommendation to explain
 * @returns Indonesian language explanation suitable for middle school students
 */
export function getCalculationExplanation(
  scores: RiasecScores,
  recommendation: Recommendation
): string {
  const config = SPECIALIZATION_CONFIGS.find((c) => c.id === recommendation.id);
  
  if (!config) {
    return 'Penjelasan tidak tersedia untuk rekomendasi ini.';
  }
  
  // Map category codes to Indonesian names
  const categoryNames: Record<keyof RiasecScores, string> = {
    r: 'Realistic (Realistis)',
    i: 'Investigative (Investigatif)',
    a: 'Artistic (Artistik)',
    s: 'Social (Sosial)',
    e: 'Enterprising (Enterprising)',
    c: 'Conventional (Konvensional)',
  };
  
  // Get categories with non-zero weights
  const relevantCategories = (Object.entries(config.weights) as [keyof CategoryWeights, number][])
    .filter(([_, weight]) => weight > 0)
    .sort((a, b) => b[1] - a[1]); // Sort by weight descending
  
  if (relevantCategories.length === 0) {
    return 'Tidak ada kategori yang relevan untuk peminatan ini.';
  }
  
  // Build explanation
  let explanation = `Rekomendasi ${recommendation.name} dihitung berdasarkan kategori RIASEC berikut:\n\n`;
  
  relevantCategories.forEach(([category, weight]) => {
    const score = scores[category];
    const categoryName = categoryNames[category];
    const weightPercent = Math.round(weight * 100);
    
    explanation += `• ${categoryName}: Skor kamu ${score}/11 (bobot ${weightPercent}%)\n`;
  });
  
  explanation += `\nPersentase kesesuaian dihitung dengan rumus sederhana: `;
  explanation += `(total skor berbobot / total skor maksimal berbobot) × 100%\n\n`;
  explanation += `Hasilnya: ${recommendation.matchPercentage}% kesesuaian dengan ${recommendation.name}`;
  
  return explanation;
}
